<template>
    <vx-modal v-model:visible="visible" padding="standard" :preventClosing="changingAssignee" size="md">
        <template v-slot:header>Assign To</template>

        <div class="flex flex-col gap-2">
            <vx-alert v-if="error" color="danger" :closeable="false">
                {{ error }}
            </vx-alert>

            <div class="flex flex-col gap-2">
                <vx-radio-selection-item
                    v-if="hasScope('threads:claim')"
                    data-test="assign-to-self"
                    :active="assigneeId === currentUserId"
                    @selected="assigneeId = currentUserId"
                    >Assign to myself</vx-radio-selection-item
                >
                <vx-radio-selection-item
                    v-if="hasScope('threads:assign')"
                    data-test="assign-to-someone"
                    :active="assigneeId !== currentUserId"
                    @selected="assigneeId = null"
                    >Assign to someone else</vx-radio-selection-item
                >
            </div>

            <div v-if="assigneeId !== currentUserId" class="mt-4">
                <vx-label>Assign to</vx-label>
                <vx-selectable
                    v-model="assigneeId"
                    :clearable="false"
                    size="lg"
                    relative-dropdown
                    label="name"
                    :reduce="(option) => option.id"
                    :options="agentsOptions"
                >
                </vx-selectable>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" @click="visible = false"> Cancel </vx-button>

                <vx-button
                    class="grow"
                    @click.prevent="submit()"
                    :loading="changingAssignee"
                    color="primary"
                    data-test="submit-button"
                    size="lg"
                    type="button"
                >
                    Done
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxAlert, VxButton, VxLabel, VxModal, VxRadioSelectionItem, VxSelectable } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { useConversation } from '~/composables/useConversation';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { hasScope } from '~/utils/users';

const currentUserId = Spark.state.user.id;
const { agents } = useMessageHub();
const { activeThread, assignThread, unassignThread, changingAssignee } = useConversation();

const props = defineProps({
    emitOnly: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['submit']);

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const assigneeId = ref(currentUserId);

const error = ref('');

const agentsOptions = computed(() => {
    return [
        {
            id: 'unassigned',
            name: 'None (Unassigned)',
        },
        ...agents.value.filter((a) => a.assignee_id !== Spark.state.user.id),
    ];
});

const submit = async () => {
    if (!assigneeId.value) {
        error.value = 'Please select an agent.';
        return;
    }

    if (props.emitOnly) {
        // For emit-only mode: just emit the assignee ID
        emit('submit', assigneeId.value);
        visible.value = false;
        return;
    }

    if (!activeThread.value.contact_id) {
        error.value = 'Please select a conversation first.';
        return;
    }

    const request =
        assigneeId.value === 'unassigned'
            ? unassignThread(activeThread.value)
            : assignThread(activeThread.value, assigneeId.value);

    await request;
    visible.value = false;
};

watch(
    visible,
    (visible) => {
        if (!visible) {
            return;
        }

        if (props.emitOnly) {
            assigneeId.value = currentUserId;
        } else {
            assigneeId.value = activeThread.value.assignee_id || currentUserId;
        }
    },
    {
        immediate: true,
    }
);
</script>
